import { renderRichTextToComponent } from "@components/cms/rich-text-renderer/renderRichTextToComponent";
import { activeSiteConfig } from "@config/site/site-config";
import type { IPageGeneric } from "@contentful-api/types/contentful";
import type {
    LineItem,
    ProductVariant,
    ProductVariantAvailabilityWithChannel,
} from "@graphql/generated/components";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { storeLocatorPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { H } from "@ui/components/content/heading/A11yHeading";
import { Button, ButtonGroup } from "@ui/components/forms/button/Button";
import { AspectRatio } from "@ui/components/layout/aspect-ratio/AspectRatio";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid, GridItem } from "@ui/components/layout/grid/Grid";
import { HStack } from "@ui/components/layout/stack/Stack";
import { scrollbarStyles } from "@ui/css/01.scrollbar/scrollbar.styles";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import { isEmpty } from "lodash-es";
import { useRouter } from "next/router";
import { useState } from "react";
import { Capture } from "./capture";
import { CardItem } from "./CardItem";
import { CardItems } from "./CardItems";
import { type StoreData, Provider } from "./controller";
import { FilterDropdowns } from "./FilterDropdowns";
import { filterValuesDistance, filterValueStoreType } from "./filterValues";
import { Search } from "./search";
import { StoreCount } from "./StoreCount";
import { StoreList } from "./StoreList";
import { StoreLocatorMap } from "./StoreLocatorMap";
import { StoreModal } from "./StoreModal";
import {
    MAP_TAB_SWITCH_LIST,
    MAP_TAB_SWITCH_MAP,
} from "@e2e/helpers/store-locator/constants/common";

interface StoreLocatorProps {
    page?: IPageGeneric;
    storesWithStock?: ProductVariantAvailabilityWithChannel[];
    selectedProductData?: any | LineItem | LineItem[];
    selectedVariant?: ProductVariant;
    onClose?: () => void;
}

export const StoreLocator = ({
    page,
    storesWithStock,
    selectedProductData,
    onClose,
    selectedVariant,
}: StoreLocatorProps) => {
    const { getMultiple } = useMicrocopy();
    const { pathname } = useRouter();
    const { xl: isExtraLargeViewport, "2xl": isTwoExtraLargeViewport } = useThemeMediaQuery();
    // If we have a selectedVariant, that means will be StoreLocator for C&C.
    // The default behavior of the StoreLocator is to show the list view, on C&C, otherwise show the map.
    const [tab, setTab] = useState<"list" | "map">("map");
    const pageDescription = page?.fields?.desc;

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.headline,
            ClickAndCollect.storeLocatorDescription,
            ClickAndCollect.storeLocatorList,
            ClickAndCollect.storeLocatorMap,
            ClickAndCollect.storesWithin,
        ]);
    })();

    const isMapTab = tab === "map";
    const { country } = activeSiteConfig;

    const isClickAndCollect = !Object.values(storeLocatorPage).find((path) =>
        pathname.includes(path)
    );

    return (
        <>
            <Flex
                flexDirection="column"
                gap={[3, 3, 4]}
                px={[3, 0]}
                maxW={["80%", "100%"]}
                mb={[6, 12]}
                mt={[6, 8]}
                align="start"
            >
                {!isEmpty(page?.fields?.title) ? (
                    <H ml={[3, 6]} as="h1" size="h1">
                        {page?.fields?.title}
                    </H>
                ) : (
                    <H as="h1" size="h6">
                        {microcopies[ClickAndCollect.headline]}
                    </H>
                )}
                {(!isEmpty(pageDescription) ||
                    !isEmpty(microcopies[ClickAndCollect.storeLocatorDescription])) && (
                    <Box>
                        {pageDescription
                            ? renderRichTextToComponent(pageDescription)
                            : microcopies[ClickAndCollect.storeLocatorDescription]}
                    </Box>
                )}
            </Flex>
            <Provider initStores={storesWithStock as unknown as StoreData[]}>
                <Grid
                    templateColumns={[
                        `repeat(4, 1fr)`,
                        `repeat(6, 1fr)`,
                        `repeat(6, 1fr)`,
                        `repeat(6, 1fr)`,
                        `repeat(12, 1fr)`,
                        `repeat(12, 1fr)`,
                    ]}
                    flex={1}
                    px={{ base: 0, xl: isClickAndCollect ? 0 : 6 }}
                    mb={{ base: 6, xl: 14 }}
                >
                    <GridItem
                        gridRow={[1]}
                        colStart={[1]}
                        colSpan={[4, 6, 6, 6, 4, 4]}
                        sx={{
                            ...(isExtraLargeViewport && { contain: "size" }),
                            overflow: "hidden",
                        }}
                    >
                        <Flex
                            direction="column"
                            align="stretch"
                            maxHeight="100%"
                            h={[
                                // TODO: Refactor this to make adapt all the mediaqueries in a responsive way.
                                !isMapTab ? "95vh" : "full",
                                !isMapTab ? "95vh" : "full",
                                !isMapTab ? "95vh" : "full",
                                !isMapTab ? "95vh" : "full",
                                !isMapTab ? "95vh" : "full",
                                "full",
                            ]}
                        >
                            {isClickAndCollect &&
                                (selectedProductData && selectedProductData?.length >= 1 ? (
                                    <Box px={[3, 0]} mb={6}>
                                        <CardItems selectedProductData={selectedProductData} />
                                    </Box>
                                ) : (
                                    <Box px={[3, 0]} mb={6}>
                                        <CardItem selectedProductData={selectedProductData} />
                                    </Box>
                                ))}
                            <Search isClickAndCollect={isClickAndCollect} />
                            <HStack
                                justifyContent="space-between"
                                pt={3}
                                pb={4}
                                px={{ base: 3, xl: 0 }}
                            >
                                <Capture />
                            </HStack>
                            {!isClickAndCollect && (
                                <>
                                    <Flex mx={[3, 0]} gap={"0.2"} mb={4}>
                                        <FilterDropdowns
                                            // If the country is US or JP, we show the US/JP filter with different values, otherwise we show the CA/Default filter values with km/english
                                            values={
                                                filterValuesDistance[
                                                    country === "US" || country === "JP"
                                                        ? country
                                                        : "Default"
                                                ]
                                            }
                                            filterType={"distance"}
                                        />
                                        <FilterDropdowns
                                            values={filterValueStoreType}
                                            filterType={"storeType"}
                                        />
                                    </Flex>
                                    {isExtraLargeViewport && (
                                        <StoreCount
                                            microcopy={microcopies[ClickAndCollect.storesWithin]}
                                        />
                                    )}
                                </>
                            )}
                            <Flex
                                alignItems={"center"}
                                mx={3}
                                mb={4}
                                justifyContent={"space-between"}
                            >
                                {!isExtraLargeViewport && (
                                    <>
                                        <StoreCount
                                            isExtraLargeViewport={isExtraLargeViewport}
                                            microcopy={microcopies[ClickAndCollect.storesWithin]}
                                        />
                                        <ButtonGroup spacing="px" ml={"auto"}>
                                            <Button
                                                size="xs"
                                                variant={tab === "list" ? "solid" : "tertiary"}
                                                onClick={() => setTab("list")}
                                                fontSize="mobileLinkL1"
                                                data-testid={MAP_TAB_SWITCH_LIST}
                                            >
                                                <span>
                                                    {microcopies[ClickAndCollect.storeLocatorList]}
                                                </span>
                                            </Button>
                                            <Button
                                                size="xs"
                                                variant={tab === "map" ? "solid" : "tertiary"}
                                                onClick={() => setTab("map")}
                                                fontSize="mobileLinkL1"
                                                data-testid={MAP_TAB_SWITCH_MAP}
                                            >
                                                <span>
                                                    {microcopies[ClickAndCollect.storeLocatorMap]}
                                                </span>
                                            </Button>
                                        </ButtonGroup>
                                    </>
                                )}
                            </Flex>
                            <Flex flex={1} direction="column" overflow="hidden" display="flex">
                                <Box
                                    flex={1}
                                    overflow="auto"
                                    pr={{ base: 0, xl: 3 }}
                                    mr={{ base: 0, xl: 3 }}
                                    sx={{
                                        div: {
                                            ...scrollbarStyles,
                                        },
                                    }}
                                    px={{ base: 3, xl: 0 }}
                                    h="full"
                                >
                                    {!isExtraLargeViewport && (
                                        <StoreList
                                            hideLoader={isMapTab}
                                            selectedVariant={selectedVariant}
                                            onClose={onClose}
                                            storesWithStock={storesWithStock}
                                        />
                                    )}
                                    {isExtraLargeViewport && (
                                        <StoreList
                                            selectedVariant={selectedVariant}
                                            onClose={onClose}
                                        />
                                    )}
                                </Box>
                            </Flex>
                        </Flex>
                    </GridItem>
                    <GridItem
                        colStart={isMapTab ? [] : [1]}
                        gridRow={isMapTab ? [] : [1]}
                        colSpan={[4, 6, 6, 6, 8]}
                    >
                        {!isExtraLargeViewport && (
                            <AspectRatio ratio="2/3" left={isMapTab ? 0 : "-100%"}>
                                <StoreLocatorMap
                                    // We are using this value only to determine the zoom levels for the map
                                    isExtraLargeViewport={isTwoExtraLargeViewport}
                                    storesWithStock={storesWithStock}
                                />
                            </AspectRatio>
                        )}
                        {isExtraLargeViewport && (
                            // We need to calculate the total height of the viewport - the height of the header and the navigation - the space below the map and footer
                            <Box minHeight={"calc(100vh - 260px)"} position="relative">
                                <StoreLocatorMap
                                    isExtraLargeViewport={isTwoExtraLargeViewport}
                                    storesWithStock={storesWithStock}
                                />
                            </Box>
                        )}
                    </GridItem>
                    <StoreModal onClose={onClose} selectedVariant={selectedVariant} />
                </Grid>
            </Provider>
        </>
    );
};
